import openai from './openaiClient';

/**
 * Generates contract templates using OpenAI with client data, property details, and retainer percentages.
 * Maintains Arabic RTL formatting compliance.
 */

/**
 * Generate marketing contract template with OpenAI
 * @param {Object} contractData - Contract details including client, property, and retainer info
 * @returns {Promise<Object>} Generated contract HTML and metadata
 */
export async function generateMarketingContractTemplate(contractData) {
  try {
    const {
      clientName,
      clientId,
      clientPhone,
      clientEmail,
      propertyTitle,
      propertyType,
      propertyAddress,
      propertyCity,
      propertyArea,
      targetPrice,
      minimumPrice,
      arddjoRetainerPercentage,
      ownerRetainerPercentage,
      marketingDurationMonths,
      exclusiveRights,
      marketingChannels = [],
      specialTerms = '',
      includeLogo = true,
      includeWatermark = false
    } = contractData;

    // Get company branding for logo inclusion
    let companyBranding = null;
    if (includeLogo || includeWatermark) {
      try {
        const { getCompanyBranding } = await import('./companySettingsService');
        companyBranding = await getCompanyBranding();
      } catch (error) {
        console.warn('Could not load company branding:', error);
      }
    }

    const prompt = `أنت خبير قانوني متخصص في صياغة عقود التسويق العقاري باللغة العربية وفقاً للقوانين الأردنية.

قم بإنشاء عقد تسويق عقاري احترافي كامل باللغة العربية الفصحى بصيغة قانونية.

${includeLogo && companyBranding ? `**معلومات الشركة:**
- اسم الشركة: ${companyBranding?.companyName}
` : ''}

**معلومات العقد:**
- نوع العقد: عقد تسويق عقاري
- اسم المالك: ${clientName || '[اسم المالك]'}
- رقم الهوية: ${clientId || '[رقم الهوية]'}
- رقم الهاتف: ${clientPhone || '[رقم الهاتف]'}
- البريد الإلكتروني: ${clientEmail || '[البريد الإلكتروني]'}

**معلومات العقار:**
- اسم العقار: ${propertyTitle || '[اسم العقار]'}
- نوع العقار: ${propertyType || '[نوع العقار]'}
- العنوان: ${propertyAddress || '[العنوان]'}
- المدينة: ${propertyCity || '[المدينة]'}
- المساحة: ${propertyArea || '[المساحة]'} متر مربع
- السعر المستهدف: ${targetPrice || '[السعر المستهدف]'} دينار أردني
- الحد الأدنى للسعر: ${minimumPrice || '[الحد الأدنى]'} دينار أردني

**شروط التسويق:**
- نسبة عمولة ARDDJO: ${arddjoRetainerPercentage || '[نسبة ARDDJO]'}%
- نسبة عمولة المالك: ${ownerRetainerPercentage || '[نسبة المالك]'}%
- مدة التسويق: ${marketingDurationMonths || '6'} أشهر
- حقوق التسويق: ${exclusiveRights ? 'حصرية' : 'غير حصرية'}
${marketingChannels?.length ? `- قنوات التسويق: ${marketingChannels?.join('، ')}` : ''}
${specialTerms ? `- شروط خاصة: ${specialTerms}` : ''}

**متطلبات الصياغة:**
1. استخدم اللغة العربية الفصحى القانونية الاحترافية
2. اتبع التنسيق القانوني الأردني المعتمد
3. أضف بنود واضحة لتوزيع العمولات بين ARDDJO والمالك
4. حدد التزامات كل طرف بوضوح
5. أضف بنود الإنهاء المبكر والشروط الجزائية
6. أضف بند حماية البيانات والسرية
7. أضف مساحات للتوقيعات والتواريخ
8. رقم جميع البنود والفقرات بشكل واضح

**الهيكل المطلوب:**
- ديباجة العقد
- بيانات الأطراف (ARDDJO والمالك)
- موضوع العقد ووصف العقار
- شروط التسويق والعمولات
- التزامات ARDDJO (التسويق، الإعلان، العروض)
- التزامات المالك (توفير المعلومات، السماح بالمعاينات)
- توزيع العمولات والنسب المئوية
- مدة العقد وشروط التجديد
- شروط الإنهاء والإلغاء
- التوقيعات والتواريخ

قم بإنشاء العقد الكامل بصيغة HTML منسقة مع استخدام علامات <div> و <p> و <h1> و <h2> و <h3> للتنسيق. استخدم dir="rtl" للنصوص العربية وتأكد من التنسيق الصحيح من اليمين لليسار.`;

    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: 'أنت خبير قانوني متخصص في صياغة العقود العقارية باللغة العربية. تلتزم بالمعايير القانونية الأردنية وتستخدم لغة قانونية احترافية مع الحفاظ على تنسيق RTL للنصوص العربية.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 3000
      });

      const generatedContent = response?.choices?.[0]?.message?.content;

      return {
        htmlContent: generatedContent,
        metadata: {
          generatedAt: new Date()?.toISOString(),
          model: 'gpt-4o',
          contractType: 'marketing'
        }
      };
    } catch (apiError) {
      // Handle OpenAI API errors with user-friendly messages
      if (apiError?.status === 429) {
        throw new Error('تم تجاوز حد استخدام OpenAI API. يرجى التحقق من خطة الاشتراك أو المحاولة لاحقاً.');
      } else if (apiError?.status === 401) {
        throw new Error('خطأ في مفتاح OpenAI API. يرجى التحقق من إعدادات النظام.');
      } else if (apiError?.status === 500 || apiError?.status === 503) {
        throw new Error('خدمة OpenAI غير متاحة حالياً. يرجى المحاولة لاحقاً.');
      } else {
        throw new Error(`خطأ في إنشاء العقد: ${apiError?.message || 'خطأ غير معروف'}`);
      }
    }
  } catch (error) {
    console.error('Error generating marketing contract template:', error);
    
    // Handle specific OpenAI errors
    if (error?.status === 429 || error?.code === 'insufficient_quota') {
      throw new Error('تم تجاوز حد استخدام خدمة الذكاء الاصطناعي. يرجى المحاولة لاحقاً أو التواصل مع الدعم الفني.');
    } else if (error?.status === 401) {
      throw new Error('خطأ في مفتاح API للذكاء الاصطناعي. يرجى التواصل مع الدعم الفني.');
    } else if (error?.status === 503 || error?.code === 'service_unavailable') {
      throw new Error('خدمة الذكاء الاصطناعي غير متاحة حالياً. يرجى المحاولة لاحقاً.');
    }
    
    throw new Error(error?.message || 'فشل في إنشاء قالب عقد التسويق');
  }
}

/**
 * Generate buyer representation contract template with OpenAI
 * @param {Object} contractData - Contract details including client, property preferences, and retainer info
 * @returns {Promise<Object>} Generated contract HTML and metadata
 */
export async function generateBuyerRepresentationContractTemplate(contractData) {
  try {
    const {
      clientName,
      clientId,
      clientPhone,
      clientEmail,
      propertyTypes = [],
      budgetMin,
      budgetMax,
      preferredLocations = [],
      arddjoRetainerPercentage,
      clientRetainerPercentage,
      representationDurationMonths,
      exclusiveRepresentation,
      specialRequirements = ''
    } = contractData;

    const prompt = `أنت خبير قانوني متخصص في صياغة عقود تمثيل المشترين العقارية باللغة العربية وفقاً للقوانين الأردنية.

قم بإنشاء عقد تمثيل مشتري عقاري احترافي كامل باللغة العربية الفصحى بصيغة قانونية.

**معلومات العميل:**
- اسم العميل: ${clientName || '[اسم العميل]'}
- رقم الهوية: ${clientId || '[رقم الهوية]'}
- رقم الهاتف: ${clientPhone || '[رقم الهاتف]'}
- البريد الإلكتروني: ${clientEmail || '[البريد الإلكتروني]'}

**تفضيلات العقار:**
${propertyTypes?.length ? `- أنواع العقارات المطلوبة: ${propertyTypes?.join('، ')}` : '- أنواع العقارات المطلوبة: [أنواع العقارات]'}
- الميزانية الدنيا: ${budgetMin || '[الحد الأدنى]'} دينار أردني
- الميزانية القصوى: ${budgetMax || '[الحد الأقصى]'} دينار أردني
${preferredLocations?.length ? `- المواقع المفضلة: ${preferredLocations?.join('، ')}` : '- المواقع المفضلة: [المواقع]'}
${specialRequirements ? `- متطلبات خاصة: ${specialRequirements}` : ''}

**شروط التمثيل:**
- نسبة عمولة ARDDJO: ${arddjoRetainerPercentage || '[نسبة ARDDJO]'}%
- نسبة مساهمة العميل: ${clientRetainerPercentage || '[نسبة العميل]'}%
- مدة التمثيل: ${representationDurationMonths || '6'} أشهر
- نوع التمثيل: ${exclusiveRepresentation ? 'حصري' : 'غير حصري'}

**متطلبات الصياغة:**
1. استخدم اللغة العربية الفصحى القانونية الاحترافية
2. اتبع التنسيق القانوني الأردني المعتمد
3. أضف بنود واضحة لتوزيع العمولات بين ARDDJO والعميل
4. حدد نطاق خدمات التمثيل بوضوح
5. أضف بنود البحث والمعاينة والتفاوض
6. أضف بنود الإنهاء المبكر والشروط الجزائية
7. أضف بند حماية البيانات والسرية
8. أضف مساحات للتوقيعات والتواريخ
9. رقم جميع البنود والفقرات بشكل واضح

**الهيكل المطلوب:**
- ديباجة العقد
- بيانات الأطراف (ARDDJO والعميل)
- موضوع العقد ونطاق التمثيل
- تفضيلات العقار والميزانية
- خدمات ARDDJO (البحث، المعاينة، التفاوض، الإجراءات)
- التزامات العميل (التعاون، توفير المعلومات، الجدية)
- العمولات والنسب المئوية
- مدة العقد وشروط التجديد
- شروط الإنهاء والإلغاء
- التوقيعات والتواريخ

قم بإنشاء العقد الكامل بصيغة HTML منسقة مع استخدام علامات <div> و <p> و <h1> و <h2> و <h3> للتنسيق. استخدم dir="rtl" للنصوص العربية وتأكد من التنسيق الصحيح من اليمين لليسار.`;

    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: 'أنت خبير قانوني متخصص في صياغة عقود تمثيل المشترين العقارية باللغة العربية. تلتزم بالمعايير القانونية الأردنية وتستخدم لغة قانونية احترافية مع الحفاظ على تنسيق RTL للنصوص العربية.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 3000
      });

      const generatedContent = response?.choices?.[0]?.message?.content;

      return {
        htmlContent: generatedContent,
        metadata: {
          generatedAt: new Date()?.toISOString(),
          model: 'gpt-4o',
          contractType: 'buyer_representation'
        }
      };
    } catch (apiError) {
      // Handle OpenAI API errors with user-friendly messages
      if (apiError?.status === 429) {
        throw new Error('تم تجاوز حد استخدام OpenAI API. يرجى التحقق من خطة الاشتراك أو المحاولة لاحقاً.');
      } else if (apiError?.status === 401) {
        throw new Error('خطأ في مفتاح OpenAI API. يرجى التحقق من إعدادات النظام.');
      } else if (apiError?.status === 500 || apiError?.status === 503) {
        throw new Error('خدمة OpenAI غير متاحة حالياً. يرجى المحاولة لاحقاً.');
      } else {
        throw new Error(`خطأ في إنشاء العقد: ${apiError?.message || 'خطأ غير معروف'}`);
      }
    }
  } catch (error) {
    console.error('Error generating buyer representation contract template:', error);
    
    // Handle specific OpenAI errors
    if (error?.status === 429 || error?.code === 'insufficient_quota') {
      throw new Error('تم تجاوز حد استخدام خدمة الذكاء الاصطناعي. يرجى المحاولة لاحقاً أو التواصل مع الدعم الفني.');
    } else if (error?.status === 401) {
      throw new Error('خطأ في مفتاح API للذكاء الاصطناعي. يرجى التواصل مع الدعم الفني.');
    } else if (error?.status === 503 || error?.code === 'service_unavailable') {
      throw new Error('خدمة الذكاء الاصطناعي غير متاحة حالياً. يرجى المحاولة لاحقاً.');
    }
    
    throw new Error(error?.message || 'فشل في إنشاء قالب عقد تمثيل المشتري');
  }
}

/**
 * Generate general property contract template with OpenAI
 * @param {Object} contractData - Contract details
 * @returns {Promise<Object>} Generated contract HTML and metadata
 */
export async function generateGeneralContractTemplate(contractData) {
  try {
    const {
      contractType = 'rental',
      partyA,
      partyB,
      clientName,
      clientId,
      clientPhone,
      clientEmail,
      propertyTitle,
      propertyType,
      propertyAddress,
      propertyCity,
      propertyArea,
      amount,
      duration,
      startDate,
      endDate,
      paymentMethod,
      deposit,
      specialTerms = ''
    } = contractData;

    const contractTypeArabic = {
      rental: 'إيجار',
      sale: 'بيع',
      lease: 'تأجير',
      management: 'إدارة'
    }?.[contractType] || 'إيجار';

    const prompt = `أنت خبير قانوني متخصص في صياغة العقود العقارية باللغة العربية وفقاً للقوانين الأردنية.

قم بإنشاء عقد ${contractTypeArabic} عقاري احترافي كامل باللغة العربية الفصحى بصيغة قانونية.

**معلومات العقد:**
- نوع العقد: عقد ${contractTypeArabic}
- الطرف الأول: ${partyA || '[الطرف الأول]'}
- الطرف الثاني: ${partyB || clientName || '[الطرف الثاني]'}
${clientId ? `- رقم الهوية: ${clientId}` : ''}
${clientPhone ? `- رقم الهاتف: ${clientPhone}` : ''}
${clientEmail ? `- البريد الإلكتروني: ${clientEmail}` : ''}

**معلومات العقار:**
- اسم العقار: ${propertyTitle || '[اسم العقار]'}
- نوع العقار: ${propertyType || '[نوع العقار]'}
- العنوان: ${propertyAddress || '[العنوان]'}
- المدينة: ${propertyCity || '[المدينة]'}
- المساحة: ${propertyArea || '[المساحة]'} متر مربع

**الشروط المالية:**
- ${contractType === 'rental' ? 'قيمة الإيجار' : 'قيمة البيع'}: ${amount || '[المبلغ]'} دينار أردني
${duration ? `- مدة العقد: ${duration}` : ''}
${startDate ? `- تاريخ البدء: ${startDate}` : ''}
${endDate ? `- تاريخ الانتهاء: ${endDate}` : ''}
${paymentMethod ? `- طريقة الدفع: ${paymentMethod}` : ''}
${deposit ? `- مبلغ التأمين: ${deposit} دينار أردني` : ''}
${specialTerms ? `- شروط خاصة: ${specialTerms}` : ''}

**متطلبات الصياغة:**
1. استخدم اللغة العربية الفصحى القانونية الاحترافية
2. اتبع التنسيق القانوني الأردني المعتمد
3. أضف بنود واضحة لحقوق وواجبات كل طرف
4. أضف بنود الدفع والجداول المالية
5. أضف بنود الصيانة والمسؤوليات
6. أضف بنود الإنهاء والتجديد
7. أضف مساحات للتوقيعات والتواريخ
8. رقم جميع البنود والفقرات بشكل واضح

**الهيكل المطلوب:**
- ديباجة العقد
- بيانات الأطراف
- موضوع العقد ووصف العقار
- الشروط والأحكام المالية
- حقوق وواجبات الطرفين
- شروط الإنهاء والتجديد
- التوقيعات والتواريخ

قم بإنشاء العقد الكامل بصيغة HTML منسقة مع استخدام علامات <div> و <p> و <h1> و <h2> و <h3> للتنسيق. استخدم dir="rtl" للنصوص العربية وتأكد من التنسيق الصحيح من اليمين لليسار.`;

    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: 'أنت خبير قانوني متخصص في صياغة العقود العقارية باللغة العربية. تلتزم بالمعايير القانونية الأردنية وتستخدم لغة قانونية احترافية مع الحفاظ على تنسيق RTL للنصوص العربية.'
          },
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 3000
      });

      const generatedContent = response?.choices?.[0]?.message?.content;

      return {
        htmlContent: generatedContent,
        metadata: {
          generatedAt: new Date()?.toISOString(),
          model: 'gpt-4o',
          contractType: contractData?.contractType
        }
      };
    } catch (apiError) {
      // Handle OpenAI API errors with user-friendly messages
      if (apiError?.status === 429) {
        throw new Error('تم تجاوز حد استخدام OpenAI API. يرجى التحقق من خطة الاشتراك أو المحاولة لاحقاً.');
      } else if (apiError?.status === 401) {
        throw new Error('خطأ في مفتاح OpenAI API. يرجى التحقق من إعدادات النظام.');
      } else if (apiError?.status === 500 || apiError?.status === 503) {
        throw new Error('خدمة OpenAI غير متاحة حالياً. يرجى المحاولة لاحقاً.');
      } else {
        throw new Error(`خطأ في إنشاء العقد: ${apiError?.message || 'خطأ غير معروف'}`);
      }
    }
  } catch (error) {
    console.error('Error generating general contract template:', error);
    
    // Handle specific OpenAI errors
    if (error?.status === 429 || error?.code === 'insufficient_quota') {
      throw new Error('تم تجاوز حد استخدام خدمة الذكاء الاصطناعي. يرجى المحاولة لاحقاً أو التواصل مع الدعم الفني.');
    } else if (error?.status === 401) {
      throw new Error('خطأ في مفتاح API للذكاء الاصطناعي. يرجى التواصل مع الدعم الفني.');
    } else if (error?.status === 503 || error?.code === 'service_unavailable') {
      throw new Error('خدمة الذكاء الاصطناعي غير متاحة حالياً. يرجى المحاولة لاحقاً.');
    }
    
    throw new Error(error?.message || 'فشل في إنشاء قالب العقد');
  }
}
